/**
 * **A port of `legacy/frontend/src/screens/MessagesScreen.tsx`, whole but for its `:3-9`.** F-026 T5:
 * «Сообщения от бота», what the bot writes to every member and when — the regent's own, pushed from
 * the choir tab's hub by `ChoirScreen`'s `messages` arm (T13), behind the hub's tile of that name.
 *
 * Their `:3-9` is one import mixing the values `ApiError` and `api` with the types `MessageRule`,
 * `ScheduledMessageDraft` and `ScheduledMessageRow` from `../api`. The types are server shapes, so
 * here they come from `@contracts/index` (ADR-0011) and the line splits in two. The contract names
 * the draft `ScheduledMessageRequest` (`legacy/backend/app/schemas.py:858`); it is imported under
 * the old client's name, so their `:136` — `payload`'s return type — stays theirs byte for byte.
 * Their `:1-2` and their `:10` to the end are theirs, and `scripts/ported-partial.txt` holds both
 * regions.
 *
 * Carried as it is, every one `KEEP` (human, 2026-09-21): «Каждый среда» and the rest of the
 * nominative weekdays (`FND-946`); the hint's literal «за 3 часа» whatever the reminder says
 * (`FND-947`, until C-014 removed the hint — `FND-1609`); the default reminder deleted or re-ruled
 * like any other row (`FND-948`, `FND-949`);
 * «всему хору» and «каждому» for senders that reach only who allowed the bot (`FND-950`); none of
 * the server's numeric bounds held by the sheet (`FND-951`); refusals outliving their context
 * (`FND-952`); a write whose reload fails reported as success (`FND-953`); a `once` card with no
 * date, «включено» and counted after it fires (`FND-955`); «Сообщения не пришло» from `Failed`
 * (`FND-957`); and the stylesheet's comment naming `--ink-quiet` (`FND-958`, `index.css`'s).
 *
 * The load's refusal text (their `:177-182`) is ported as written and read by nothing: `Failed`
 * takes no text and `refusal()` is never called with `'screen'` — held by review, `FND-954`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { MessageRule, ScheduledMessageRequest as ScheduledMessageDraft, ScheduledMessageRow } from '@contracts/index';
import { ApiError, api } from '../api';
import { PencilIcon, PlusIcon } from '../icons';
import { useBackKey } from '../backKey';
import Ask from './Ask';
import Sheet from './Sheet';
import { Failed, Settling, Skeleton } from './States';
import { DateWheel, TimeWheel } from './TimeWheel';

/**
 * The five rules, in the order the sheet offers them.
 *
 * `before` is last and takes the whole width: it is the odd one of the set —
 * everything above it is a clock, and this one hangs off a gathering — and its
 * words do not fit half a row anyway.
 */
const RULES: [MessageRule, string][] = [
  ['weekly', 'Каждую неделю'],
  ['daily', 'Каждый день'],
  ['monthly', 'Раз в месяц'],
  ['once', 'Разово'],
  ['before', 'За N часов до начала'],
];

/** Monday first, which is the week this choir keeps. */
const DAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const DAY_NAMES = [
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
  'воскресенье',
];

/** «09:00:00» from the server, «09:00» in a field and on a card. */
function clock(at: string | null): string {
  return at === null ? '' : at.slice(0, 5);
}

/**
 * The rule at the head of a card: «Каждый четверг», «25 числа», «За 3 ч до начала».
 *
 * No kind is named in the last one, and that is the owner's decision of
 * 2026-08-24 rather than an omission: the reminder goes out before a rehearsal
 * and before a service alike — his call of 2026-08-19 — and this app invents no
 * umbrella word for the two. The handoff wrote «до спевки», which taken
 * literally would have stopped the bot before every service the choir sings at.
 */
function ruleWord(message: ScheduledMessageRow): string {
  switch (message.rule) {
    case 'daily':
      return 'Каждый день';
    case 'weekly':
      return `Каждый ${DAY_NAMES[message.day_of_week ?? 0]}`;
    case 'monthly':
      return `${message.day_of_month ?? 1} числа`;
    case 'once':
      return 'Разово';
    case 'before':
      return `За ${message.hours_before ?? 3} ч до начала`;
  }
}

/** What the sheet is holding, before anything is sent. */
type Draft = {
  messageId: number | null;
  text: string;
  rule: MessageRule;
  dayOfWeek: number;
  dayOfMonth: string;
  onDate: string;
  atTime: string;
  hoursBefore: string;
};

/**
 * The next five minutes, where the hour wheel stands until somebody moves it.
 *
 * Read once and not per render: recomputing it while a finger is on the wheel
 * would move the columns under it.
 */
function nextFive(): string {
  const now = new Date();
  now.setMinutes(Math.ceil(now.getMinutes() / 5) * 5, 0, 0);
  return `${`${now.getHours()}`.padStart(2, '0')}:${`${now.getMinutes()}`.padStart(2, '0')}`;
}

/** Today, as the value a `once` rule opens on. */
function today(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function freshDraft(): Draft {
  return {
    messageId: null,
    text: '',
    rule: 'weekly',
    dayOfWeek: 0,
    dayOfMonth: '1',
    onDate: today(),
    // Empty is what «nobody has named an hour» looks like, and «Добавить
    // отправку» is dead while it lasts. The wheel under the label stands on the
    // next five minutes so there is something to read, but a wheel standing
    // somewhere is not a person choosing it.
    atTime: '',
    hoursBefore: '3',
  };
}

function draftOf(message: ScheduledMessageRow): Draft {
  return {
    messageId: message.message_id,
    text: message.text,
    rule: message.rule,
    dayOfWeek: message.day_of_week ?? 0,
    dayOfMonth: `${message.day_of_month ?? 1}`,
    onDate: message.on_date ?? today(),
    atTime: clock(message.at_time),
    hoursBefore: `${message.hours_before ?? 3}`,
  };
}

/** The draft as the server wants it: every rule carrying only its own field. */
function payload(draft: Draft): ScheduledMessageDraft {
  const before = draft.rule === 'before';
  return {
    text: draft.text.trim(),
    rule: draft.rule,
    day_of_week: draft.rule === 'weekly' ? draft.dayOfWeek : null,
    day_of_month: draft.rule === 'monthly' ? Number(draft.dayOfMonth) || 1 : null,
    on_date: draft.rule === 'once' ? draft.onDate : null,
    at_time: before ? null : draft.atTime,
    hours_before: before ? Number(draft.hoursBefore) || 3 : null,
  };
}

/**
 * «Сообщения от бота» — what the bot writes to everybody, and when.
 *
 * The regent's outright. The addressee is not a setting and never becomes one:
 * every active member, in their own chat, through the bot that already reminds
 * them. Whoever has not let it write gets nothing, which the roster says in
 * words on the screen this one is opened from.
 *
 * **One of these rows is the three-hour reminder.** It was a constant in the
 * server's code and a column nothing could write before that, and the README
 * promised the setting would come back «together with the screen that changes
 * it». This is that screen: the reminder can be rewritten, moved or switched
 * off like anything else in the list.
 */
export default function MessagesScreen({ onBack }: { onBack: () => void }) {
  const [messages, setMessages] = useState<ScheduledMessageRow[] | null>(null);
  /** The refusal and where it belongs: `screen`, `form`, or one message's id. */
  const [failure, setFailure] = useState<{ at: string | number; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  /** Which sending is being asked about, by id. */
  const [removing, setRemoving] = useState<number | null>(null);
  const clockFallback = useMemo(nextFive, []);

  const load = useCallback(async () => {
    try {
      setMessages(await api.choirMessages());
      setFailure(null);
    } catch (error) {
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить отправки',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useBackKey(onBack);

  function refusal(at: string | number) {
    return failure !== null && failure.at === at ? <p className="failure">{failure.text}</p> : null;
  }

  async function act(action: () => Promise<unknown>, at: string | number): Promise<boolean> {
    setBusy(true);
    setFailure(null);
    try {
      await action();
      await load();
      return true;
    } catch (error) {
      setFailure({
        at,
        text: error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
      });
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** One change to the draft. */
  function patch(change: Partial<Draft>): void {
    setDraft((current) => (current === null ? current : { ...current, ...change }));
  }

  const all = messages ?? [];
  const live = all.filter((message) => message.enabled).length;
  const doomed = all.find((message) => message.message_id === removing) ?? null;
  const editing = draft !== null && draft.messageId !== null;
  const noClock = draft !== null && draft.rule !== 'before' && draft.atTime.trim() === '';
  const emptyText = draft !== null && draft.text.trim() === '';

  return (
    <>
      <div className="screen-head">
        <h1 className="screen-title">Сообщения от бота</h1>
        <p className="screen-caption">бот пишет каждому в личку</p>
      </div>

      {messages === null && failure === null && <Skeleton />}
      {messages === null && failure?.at === 'screen' && (
        <Failed subject="messages" onRetry={() => void load()} />
      )}

      {messages !== null && (
        <Settling>
          <div className="block">
            {/* The switched-on ones, which is what the word «отправки» means to
                somebody reading the screen: a rule that is off is not a sending. */}
            <span className="label">
              Отправки · <span className="section-count">{live}</span>
            </span>

            <div className="cards">
              {all.map((message) => (
                <section
                  className={message.enabled ? 'card' : 'card message--off'}
                  data-tone={message.enabled ? 'accent' : 'quiet'}
                  key={message.message_id}
                >
                  <div className="split">
                    <span className="label label--tone">{ruleWord(message)}</span>
                    {/* A `before` rule has no hour of its own: it is counted off
                        whatever the gathering starts at. */}
                    <span className="tile-count">{clock(message.at_time)}</span>
                  </div>

                  <p className="message-text">{message.text}</p>

                  <div className="message-actions">
                    {/* The most reversible thing on the screen — the same capsule
                        puts it back — so it happens on the tap and nothing is
                        asked. Green is the status colour for «идёт», which is what
                        a sending that is on is doing. */}
                    <button
                      className={
                        message.enabled
                          ? 'answer answer--input'
                          : 'answer answer--input answer--blank'
                      }
                      data-tone="success"
                      aria-pressed={message.enabled}
                      disabled={busy}
                      onClick={() =>
                        void act(
                          () => api.setMessageEnabled(message.message_id, !message.enabled),
                          message.message_id,
                        )
                      }
                    >
                      {message.enabled ? 'включено' : 'выключено'}
                    </button>
                    <p className="hint">
                      {message.rule === 'before'
                        ? 'всему хору перед началом'
                        : 'всему хору в личку'}
                    </p>
                    <button
                      className="row-icon"
                      data-press="fade"
                      aria-label="Изменить отправку"
                      onClick={() => setDraft(draftOf(message))}
                    >
                      <PencilIcon size={18} />
                    </button>
                  </div>

                  {refusal(message.message_id)}
                </section>
              ))}
            </div>
            {/* The old hint under the list, «Напоминание за 3 часа до начала теперь тоже отправка
                из этого списка…», is gone: no choir has a three-hour reminder by default since
                C-001, and the line read as one (C-014, FND-1609). */}
          </div>
        </Settling>
      )}

      {messages !== null && (
        <button className="fab" onClick={() => setDraft(freshDraft())}>
          <span className="fab-word">Новая отправка</span>
          <PlusIcon size={14} cropped />
        </button>
      )}

      <Sheet
        open={draft !== null}
        label="Отправка в личку"
        onClose={() => setDraft(null)}
        foot={
          draft === null ? undefined : (
            <>
              {refusal('form')}
              <button
                className="btn btn--primary"
                disabled={busy || emptyText || noClock}
                onClick={() => {
                  const body = payload(draft);
                  const id = draft.messageId;
                  void act(
                    () => (id === null ? api.addMessage(body) : api.editMessage(id, body)),
                    'form',
                  ).then((ok) => {
                    if (ok) setDraft(null);
                  });
                }}
              >
                {editing ? 'Сохранить' : 'Добавить отправку'}
              </button>
            </>
          )
        }
      >
        {draft !== null && (
          <>
            <label className="field">
              <span>Что отправить</span>
              {/* 16px like every field in the app, and for the same reason:
                  below it iOS zooms the page in on focus and never zooms out. */}
              <textarea
                rows={4}
                maxLength={1000}
                value={draft.text}
                onChange={(event) => patch({ text: event.target.value })}
              />
            </label>

            <div className="field">
              <span>Когда</span>
              <div className="kinds">
                {RULES.map(([value, word]) => (
                  <button
                    key={value}
                    className={
                      [
                        'btn',
                        draft.rule === value ? 'btn--selected' : 'btn--outline',
                        value === 'before' ? 'kinds-wide' : null,
                      ]
                        .filter((name) => name !== null)
                        .join(' ')
                    }
                    aria-pressed={draft.rule === value}
                    onClick={() => patch({ rule: value })}
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>

            {draft.rule === 'weekly' && (
              <div className="chips">
                {DAYS.map((word, index) => (
                  <button
                    key={word}
                    className={
                      draft.dayOfWeek === index
                        ? 'answer answer--input answer--picked'
                        : 'answer answer--input answer--blank'
                    }
                    aria-pressed={draft.dayOfWeek === index}
                    onClick={() => patch({ dayOfWeek: index })}
                  >
                    {word}
                  </button>
                ))}
              </div>
            )}

            {draft.rule === 'monthly' && (
              <label className="field">
                <span>Число месяца</span>
                <input
                  inputMode="numeric"
                  value={draft.dayOfMonth}
                  onChange={(event) => patch({ dayOfMonth: event.target.value })}
                />
              </label>
            )}

            {draft.rule === 'once' && (
              <div className="field">
                <span>Дата</span>
                {/* The column starts today: a sending into the past is one that
                    never goes. */}
                <DateWheel
                  label="Дата"
                  value={draft.onDate}
                  fallback={today()}
                  onChange={(onDate) => patch({ onDate })}
                />
              </div>
            )}

            {draft.rule === 'before' && (
              <label className="field">
                <span>За сколько часов до начала</span>
                <input
                  inputMode="numeric"
                  value={draft.hoursBefore}
                  onChange={(event) => patch({ hoursBefore: event.target.value })}
                />
              </label>
            )}

            {draft.rule !== 'before' && (
              <div className="field">
                <span>Во сколько</span>
                {/* Open, with no button to open it: «Когда» hides its wheel
                    behind the dates it can guess, and a sending has no habit to
                    read — there is nothing here to offer instead. */}
                <TimeWheel
                  label="Во сколько"
                  value={draft.atTime === '' ? null : draft.atTime}
                  fallback={clockFallback}
                  step={5}
                  onChange={(atTime) => patch({ atTime })}
                />
              </div>
            )}

            <p className="hint">
              Бот напишет каждому в личку. Тем, кто не разрешил боту писать, сообщение не дойдёт —
              в составе это видно.
            </p>


            {/* The control that opens a question stays quiet where it stands:
                until the question has been asked, nothing has happened. The
                filled red is inside the `Ask` it leads to. */}
            {editing && (
              <button
                className="menu-action menu-action--danger"
                disabled={busy}
                onClick={() => {
                  // The sheet goes and the question takes its place. Two of
                  // them stacked would each register the client's back key and
                  // the inner one's cleanup would put the arrow away while the
                  // outer was still up — and a draft is not what somebody who
                  // has pressed «Удалить отправку» came back for.
                  setRemoving(draft.messageId);
                  setDraft(null);
                }}
              >
                Удалить отправку
              </button>
            )}
          </>
        )}
      </Sheet>

      <Ask
        open={doomed !== null}
        title="Удалить отправку?"
        cost={<>Текст пропадёт. Чтобы вернуть его, отправку придётся написать заново.</>}
        verb="Удалить отправку"
        busy={busy}
        failure={
          doomed !== null && failure !== null && failure.at === doomed.message_id
            ? failure.text
            : null
        }
        onConfirm={() => {
          if (doomed === null) return;
          const id = doomed.message_id;
          void act(() => api.removeMessage(id), id).then((ok) => {
            if (!ok) return;
            setRemoving(null);
            setDraft(null);
          });
        }}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}
